import type { ApolloError, NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition, Observable } from "@apollo/client/utilities";
import { createAbsintheApolloLink } from "@r26d/absinthe-apollo-link";
import * as AbsintheSocket from "@r26d/absinthe-apollo-socket";
import { createLink } from "apollo-v3-absinthe-upload-link";
import { Socket as PhoenixSocket } from "phoenix";

import { LanguagesEnum } from "globalConstants/languagesEnum";
import { getManagedWorkspaceInfo } from "hooks/useWorkspace";
import type { TWorkspaceInfo } from "hooks/useWorkspace/types";
import { SELECT_WORKSPACE_PATH } from "routes/paths";
import { URI, WS_URI } from "settings";
import { createErrorReportingLink, createSentryBreadcrumbLink } from "settings/apolloErrorLink";
import i18n from "translations/i18n";

import type { TGraphQLError } from "./types";

type GetIdToken = (options?: { forceRefresh?: boolean }) => Promise<string | undefined>;

const authRedirectMiddleware = onError(({ graphQLErrors }) => {
  graphQLErrors?.forEach((error: TGraphQLError) => {
    if (error.code === "UNAUTHORIZED") {
      window.location.href = `/${SELECT_WORKSPACE_PATH}`;
    }
  });
});

const AUTH_TOKEN_REJECTED_CODES = ["NETWORK_AUTHENTICATION_REQUIRED"];

export const isAuthTokenRejectedError = (error: unknown): boolean =>
  (error as ApolloError)?.graphQLErrors?.some((graphQLError: TGraphQLError) =>
    AUTH_TOKEN_REJECTED_CODES.includes(graphQLError.code ?? ""),
  ) ?? false;

const authRetryLink = (getIdToken: GetIdToken, onUnrecoverableAuthFailure: () => void): ApolloLink =>
  onError(({ graphQLErrors, operation, forward }) => {
    const hasRejectedToken = graphQLErrors?.some((error: TGraphQLError) =>
      AUTH_TOKEN_REJECTED_CODES.includes(error.code ?? ""),
    );

    if (!hasRejectedToken) {
      return;
    }

    if (operation.getContext().authRetried) {
      onUnrecoverableAuthFailure();
      return;
    }

    return new Observable(observer => {
      getIdToken({ forceRefresh: true })
        .then(idToken => {
          operation.setContext(({ headers = {} as Record<string, string> }) => ({
            headers: { ...headers, authorization: idToken },
            authRetried: true,
          }));

          forward(operation).subscribe(observer);
        })
        .catch(error => observer.error(error));
    });
  });

const errorReportingLink = createErrorReportingLink();

const sentryLink = createSentryBreadcrumbLink(URI);

const uploadLink = createLink({ uri: URI, fetch });

const authLink = (getIdToken: GetIdToken): ApolloLink =>
  setContext(async (_operation, { headers = {} as Record<string, string> }) => {
    const { getWorkspaceInfo } = getManagedWorkspaceInfo();

    const workspaceInfo = getWorkspaceInfo() as unknown as TWorkspaceInfo;

    const idToken = await getIdToken();

    return {
      headers: {
        ...headers,
        authorization: idToken,
        locale: headers.Locale || i18n.language || LanguagesEnum.brazilianPortuguese,
        ...(workspaceInfo ? { client_id: workspaceInfo.clientId, access_group: workspaceInfo.accessGroupSlug } : {}),
      },
    };
  });

export const generatePhoenixSocket = (getCachedIdToken: () => string | undefined): PhoenixSocket => {
  const { getWorkspaceInfo } = getManagedWorkspaceInfo();

  const workspaceInfo = getWorkspaceInfo() as unknown as TWorkspaceInfo;

  return new PhoenixSocket(WS_URI || "ws://localhost:4000/socket", {
    params: () => ({
      authorization: getCachedIdToken(),
      locale: i18n.language || LanguagesEnum.brazilianPortuguese,
      ...(workspaceInfo ? { client_id: workspaceInfo.clientId, access_group: workspaceInfo.accessGroupSlug } : {}),
    }),
  });
};

const generateApolloLink = (getIdToken: GetIdToken, onUnrecoverableAuthFailure: () => void): ApolloLink => {
  const httpLink = new HttpLink({ uri: URI, fetch });
  const link = ApolloLink.from([
    authRedirectMiddleware,
    errorReportingLink,
    sentryLink,
    authRetryLink(getIdToken, onUnrecoverableAuthFailure),
    authLink(getIdToken),
    uploadLink,
    httpLink,
  ]);
  return link;
};

const generateApolloClient = (link: ApolloLink): ApolloClient<NormalizedCacheObject> =>
  new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" }, query: { fetchPolicy: "network-only" } },
  });

export const generateApolloClientInstance = (
  getIdToken: GetIdToken,
  onUnrecoverableAuthFailure: () => void,
): ApolloClient<NormalizedCacheObject> => {
  const link = generateApolloLink(getIdToken, onUnrecoverableAuthFailure);

  return generateApolloClient(link);
};

export const generateApolloClientInstanceWithPhoenixSocket = (
  getIdToken: GetIdToken,
  onUnrecoverableAuthFailure: () => void,
  phoenixSocket: PhoenixSocket,
): ApolloClient<NormalizedCacheObject> => {
  const wsAbsintheLink = createAbsintheApolloLink(AbsintheSocket.create(phoenixSocket));
  const link = generateApolloLink(getIdToken, onUnrecoverableAuthFailure);

  const terminatingLink: ApolloLink = split(
    ({ query }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { kind, operation } = getMainDefinition(query) as any;

      return kind === "OperationDefinition" && operation === "subscription";
    },
    wsAbsintheLink,
    link,
  );

  const apolloClient = generateApolloClient(terminatingLink);

  return apolloClient;
};
